import { calculateMaximumDateOfBirth, calculateMaximumExpiryDate, calculateMinimumExpiryDate, calculateMinimumIssueDate, getFieldLabel } from '@components/panels/DocumentPanel';
import { getDocumentConfig } from '@services/identityVerification/IdentityVerification.types';
import { Country } from '@utils/constants/localisation';
import { DocumentDetail } from './documentDetailReducer';

/**
 * Allowed file extensions for document uploads
 */
export const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.png', '.jpeg', '.jpg', '.jfif', '.heif', '.heifs', '.heic',
  '.heics', '.hif'];

/**
 * Allowed file types as a comma-separated string for HTML accept attribute
 */
export const FILE_ACCEPT_TYPES = ALLOWED_FILE_EXTENSIONS.join(', ');

/**
 * Validates a document detail for required fields and date constraints.
 *
 * @param detail - The document detail to validate
 * @param panelName - Human-readable name for the panel (e.g., "Driver Licence", "Second ID")
 * @param country - Country to resolve country-specific field requirements
 * @returns Array of validation error messages (empty if everything is valid)
 */
export function validateDocumentDetail(
  detail: DocumentDetail,
  panelName: string,
  country: Country
): string[] {
  const errors: string[] = [];

  // A document type is always necessary
  if (!detail.documentType) {
    return [`${panelName} document type must be selected`];
  }

  const config = getDocumentConfig(detail.documentType, country);
  const currentDate = new Date();

  // All required fields must have values
  config.requiredFields.forEach(field => {
    const value = detail[field];
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
      const label = getFieldLabel(field, detail.documentType);
      errors.push(`${label} is required for ${detail.documentType}`);
    }
  });

  // Validate min & max constraints on any included date fields
  if (config.includedFields.includes('expiryDate') && detail.expiryDate) {
    const earliestExpiryDate = calculateMinimumExpiryDate(currentDate, config.requiresThreeMonthExpiry);
    const latestExpiryDate = calculateMaximumExpiryDate(currentDate);
    if (detail.expiryDate < earliestExpiryDate) {
      const errorMessage = config.requiresThreeMonthExpiry
        ? `${detail.documentType} Expiry Date must be at least 3 months in the future`
        : `${detail.documentType} Expiry Date must be after today`;
      errors.push(errorMessage);
    }
    if (detail.expiryDate > latestExpiryDate) {
      errors.push(`${detail.documentType} Expiry Date must be within 10 years`);
    }
  }
  if (config.includedFields.includes('dateOfBirth') && detail.dateOfBirth) {
    const latestDriverDOB = calculateMaximumDateOfBirth(currentDate);
    if (detail.dateOfBirth > latestDriverDOB) {
      errors.push(`${detail.documentType} Date of Birth must be at least 23 years ago`);
    }
    if (detail.dateOfBirth < "1900-01-01") {
      errors.push(`${detail.documentType} Date of Birth must be in 1900 or later`);
    }
  }
  if (config.includedFields.includes('issueDate') && detail.issueDate) {
    const earliestPOAIssueDate = calculateMinimumIssueDate(currentDate);
    if (detail.issueDate < earliestPOAIssueDate) {
      errors.push(`${detail.documentType} Issue Date must be within the last 3 months`);
    }
  }

  // Validate file types
  if (config.includedFields.includes('frontFile') && detail.frontFile) {
    const isValidType = ALLOWED_FILE_EXTENSIONS.some(ext =>
      detail.frontFile!.name.toLowerCase().endsWith(ext.toLowerCase())
    );
    if (!isValidType) {
      errors.push(`${panelName} file must be one of the following types: ${FILE_ACCEPT_TYPES}`);
    }
  }
  if (config.includedFields.includes('backFile') && detail.backFile) {
    const isValidType = ALLOWED_FILE_EXTENSIONS.some(ext =>
      detail.backFile!.name.toLowerCase().endsWith(ext.toLowerCase())
    );
    if (!isValidType) {
      errors.push(`${panelName} second file must be one of the following types: ${FILE_ACCEPT_TYPES}`);
    }
  }

  return errors;
}
